import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "../api/publicClient";
import FileDropzone from "../components/FileDropzone";

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const colors = {
  blue900: "#0f2d63", blue700: "#1d4ed8", blue600: "#2563eb",
  blue100: "#e6edfb", blue50: "#f3f6fd",
  green700: "#15803d", green600: "#16a34a", green100: "#dcfce7",
  amber700: "#b45309", amber100: "#fef3c7",
  red700: "#b91c1c", red100: "#fbe6e6", red50: "#fdf3f3",
  ink900: "#10151f", ink600: "#52596b", ink400: "#8892a6",
  line: "#e6e9f0", white: "#ffffff", grey100: "#f1f3f8",
};

function naira(n) {
  return "₦" + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Money maths is done in whole kobo so splits never drift by floating point.
function toCents(v) {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function fromCents(c) {
  return (c / 100).toFixed(2);
}

/**
 * Works out how an entered amount is spread across plans.
 * - The plan being paid takes up to its balance.
 * - Any remainder goes to the other unpaid plans:
 *     one candidate left  -> automatic
 *     several candidates  -> the customer picks (repeated if the pick is cleared too)
 */
function buildSplit(enteredCents, primary, others, choices) {
  const allocs = [];
  const steps = [];
  if (enteredCents <= 0) return { allocs, steps, remaining: 0, needsChoice: false };

  const first = Math.min(enteredCents, toCents(primary.balance));
  if (first > 0) allocs.push({ plan: primary, cents: first });
  let remaining = enteredCents - first;
  let pool = others.slice();
  let idx = 0;

  while (remaining > 0 && pool.length > 0) {
    let target;
    if (pool.length === 1) {
      target = pool[0];
    } else {
      const selectedId = choices[idx];
      steps.push({ candidates: pool, selectedId, remainingBefore: remaining });
      target = pool.find((p) => p.id === selectedId);
      if (!target) return { allocs, steps, remaining, needsChoice: true };
    }
    const cents = Math.min(remaining, toCents(target.balance));
    allocs.push({ plan: target, cents });
    remaining -= cents;
    pool = pool.filter((p) => p.id !== target.id);
    idx += 1;
  }
  return { allocs, steps, remaining, needsChoice: false };
}

const styles = {
  body: { margin: 0, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: colors.grey100, color: colors.ink900, minHeight: "100vh" },
  topbar: { background: colors.blue900, color: colors.white, padding: "16px 24px", display: "flex", alignItems: "center", gap: 10, fontWeight: 700 },
  topbarImg: { width: 24, height: 24, objectFit: "contain", borderRadius: 6 },
  layout: { maxWidth: 720, margin: "0 auto", padding: "28px 20px 60px" },
  panel: { background: colors.white, border: `1px solid ${colors.line}`, borderRadius: 18, padding: 22, marginBottom: 18, boxShadow: "0 1px 2px rgba(16,21,31,0.04), 0 8px 24px rgba(16,21,31,0.05)" },
  welcome: { fontSize: 24, fontWeight: 800 },
  heroLabel: { fontSize: 12, color: colors.ink400, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", marginTop: 16 },
  heroValue: { fontSize: 34, fontWeight: 800, color: colors.red700, marginTop: 2, letterSpacing: "-0.01em" },
  heroSub: { display: "flex", gap: 20, marginTop: 8, fontSize: 12.5, color: colors.ink400, flexWrap: "wrap" },
  heroSubStrong: { color: colors.ink600 },
  bankBox: { background: colors.blue50, border: `1px solid ${colors.blue100}`, borderRadius: 12, padding: 16, marginTop: 18 },
  bankRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: 13.5 },
  bankLabel: { color: colors.ink400 },
  acctCopy: { display: "flex", alignItems: "center", gap: 10 },
  copyBtn: { border: `1px solid ${colors.blue100}`, background: colors.white, color: colors.blue700, fontWeight: 700, fontSize: 12, padding: "5px 10px", borderRadius: 7, cursor: "pointer" },
  sectionTitle: { fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: colors.ink400, margin: "4px 0 12px" },

  // plan dropdown card
  planCard: { background: colors.white, border: `1px solid ${colors.line}`, borderRadius: 18, marginBottom: 14, overflow: "hidden", boxShadow: "0 1px 2px rgba(16,21,31,0.04), 0 8px 24px rgba(16,21,31,0.05)" },
  planHeader: { display: "flex", alignItems: "center", gap: 8, padding: "12px 12px 12px 14px" },
  planToggle: { display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left", color: "inherit", font: "inherit" },
  planIcon: { width: 44, height: 44, borderRadius: 12, background: colors.blue100, color: colors.blue700, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, flexShrink: 0 },
  planHeadText: { minWidth: 0 },
  planName: { fontWeight: 800, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  planProgressText: { fontSize: 12, color: colors.ink400, marginTop: 2 },
  headPayBtn: { background: colors.blue600, color: colors.white, border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 800, fontSize: 13.5, cursor: "pointer", flexShrink: 0 },
  paidBadge: { background: colors.green100, color: colors.green700, borderRadius: 999, padding: "6px 12px", fontWeight: 800, fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" },
  chevronBtn: { background: "transparent", border: "none", cursor: "pointer", padding: 6, display: "flex", alignItems: "center", color: colors.ink400, flexShrink: 0 },
  planBody: { padding: "16px 18px 18px", borderTop: `1px solid ${colors.line}` },
  progressTrack: { background: colors.grey100, borderRadius: 999, height: 7, overflow: "hidden", marginBottom: 16 },
  progressFill: { height: "100%", borderRadius: 999, background: colors.blue600 },
  paymentHistory: { maxHeight: 220, overflowY: "auto", border: `1px solid ${colors.line}`, borderRadius: 10 },
  phLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", color: colors.ink400, padding: "8px 12px 4px" },
  phRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", borderTop: `1px solid ${colors.line}` },
  phRowFirst: { borderTop: "none" },
  phAmount: { fontWeight: 700, fontSize: 13 },
  phDate: { fontSize: 11, color: colors.ink400, marginLeft: 6 },
  phStatus: { fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" },
  planFigures: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, marginBottom: 16, flexWrap: "wrap" },
  figureLabel: { fontSize: 11.5, color: colors.ink400, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" },
  figureValue: { fontSize: 17, fontWeight: 800, color: colors.ink900, marginTop: 2 },
  figureValueRed: { fontSize: 17, fontWeight: 800, color: colors.red700, marginTop: 2 },
  noHistory: { fontSize: 13, color: colors.ink400 },

  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(15, 45, 99, 0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 },
  modal: { background: colors.white, borderRadius: 16, padding: 0, width: "100%", maxWidth: 380, maxHeight: "90vh", overflowY: "auto" },
  modalInner: { padding: 22 },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  modalTitle: { fontWeight: 800, fontSize: 16 },
  modalClose: { border: "none", background: "transparent", fontSize: 18, color: colors.ink400, cursor: "pointer", lineHeight: 1 },
  receiptBox: { marginTop: 16 },
  receiptLabel: { display: "block", fontSize: 12.5, fontWeight: 700, color: colors.ink600, marginBottom: 6 },
  receiptStatus: { fontSize: 12, marginTop: 6, minHeight: 16 },
  formLabel: { display: "block", fontSize: 12.5, fontWeight: 700, color: colors.ink600, marginBottom: 6, marginTop: 16 },
  formInput: { width: "100%", border: `1px solid ${colors.line}`, borderRadius: 10, padding: "11px 13px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" },
  submitBtn: { background: colors.blue600, color: colors.white, border: "none", borderRadius: 10, padding: "11px 18px", fontWeight: 800, fontSize: 13.5, cursor: "pointer", width: "100%" },
  submitBtnDisabled: { background: colors.ink400, cursor: "not-allowed" },
  amountError: { color: colors.red700, fontSize: 12, marginTop: -6, marginBottom: 10 },
  amountHint: { color: colors.ink400, fontSize: 12, marginTop: -6, marginBottom: 10 },

  // spill-over split
  splitBox: { background: colors.blue50, border: `1px solid ${colors.blue100}`, borderRadius: 12, padding: 14, marginBottom: 12 },
  splitTitle: { fontWeight: 800, fontSize: 13 },
  splitHint: { fontSize: 12.5, color: colors.ink600, fontWeight: 600 },
  choiceCard: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", marginTop: 6, padding: "10px 12px", border: `1.5px solid ${colors.line}`, borderRadius: 10, background: colors.white, cursor: "pointer", font: "inherit", fontSize: 13, textAlign: "left", color: colors.ink900 },
  choiceCardActive: { borderColor: colors.blue600, background: colors.blue100 },
  splitSummary: { marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${colors.blue100}` },
  splitRow: { display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, padding: "3px 0" },

  messages: { maxWidth: 720, margin: "16px auto 0", padding: "0 20px" },
  msg: { borderRadius: 10, padding: "10px 14px", fontSize: 13.5, marginBottom: 8 },
  msgSuccess: { background: colors.blue50, color: colors.blue700, border: `1px solid ${colors.blue100}` },
  msgError: { background: colors.red50, color: colors.red700, border: `1px solid ${colors.red100}` },
  empty: { textAlign: "center", color: colors.ink400, padding: 16, fontSize: 13 },
};

const statusColors = {
  confirmed: { background: colors.green100, color: colors.green700 },
  pending: { background: colors.amber100, color: colors.amber700 },
  rejected: { background: colors.red100, color: colors.red700 },
};

export default function CustomerPublic() {
  const { token: rawToken } = useParams();
  const match = rawToken.match(UUID_RE);
  const token = match ? match[0] : rawToken;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [payingPlan, setPayingPlan] = useState(null);

  const load = useCallback(() => {
    publicApi
      .getCustomer(token)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  function copyAccount() {
    const text = data.account_number;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  }

  function fallbackCopy(text) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand("copy");
    } catch {
      // nothing more to do silently
    }
    document.body.removeChild(ta);
  }

  function handlePaid(planCount = 1) {
    setPayingPlan(null);
    setMessage({
      type: "success",
      text:
        "Thanks — we've recorded your payment claim. It will show as 'pending' until our accountant confirms it against the bank statement." +
        (planCount > 1 ? ` Your payment was split across ${planCount} plans.` : ""),
    });
    load();
  }

  if (error) {
    return (
      <div style={styles.body}>
        <div style={styles.topbar}>
          <img src="/otic-logo.png" alt="Otic" style={styles.topbarImg} />
          Payment Plans
        </div>
        <div style={styles.layout}>
          <div style={styles.empty}>{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.body}>
        <div style={styles.topbar}>
          <img src="/otic-logo.png" alt="Otic" style={styles.topbarImg} />
          Payment Plans
        </div>
        <div style={styles.layout}>
          <div style={styles.empty}>Loading...</div>
        </div>
      </div>
    );
  }

  const onlyOnePlan = data.plans.length === 1;

  return (
    <div style={styles.body}>
      <div style={styles.topbar}>
        <img src="/otic-logo.png" alt="Otic" style={styles.topbarImg} />
        Payment Plans
      </div>

      {message && (
        <div style={styles.messages}>
          <div style={{ ...styles.msg, ...(message.type === "success" ? styles.msgSuccess : styles.msgError) }}>
            {message.text}
          </div>
        </div>
      )}

      <div style={styles.layout}>
        <div style={styles.panel}>
          <div style={styles.welcome}>Welcome back, {data.name} </div>

          <div style={styles.heroLabel}>Outstanding Balance</div>
          <div style={styles.heroValue}>{naira(data.total_balance)}</div>
          <div style={styles.heroSub}>
            <span>Total <strong style={styles.heroSubStrong}>{naira(data.total_price)}</strong></span>
            <span>Paid <strong style={styles.heroSubStrong}>{naira(data.total_paid)}</strong></span>
            <span>{data.plans.length} plan{data.plans.length === 1 ? "" : "s"}</span>
          </div>

          <div style={styles.bankBox}>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Bank</span>
              <span>{data.bank_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account name</span>
              <span>{data.account_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account number</span>
              <span style={styles.acctCopy}>
                <span>{data.account_number}</span>
                <button type="button" style={styles.copyBtn} onClick={copyAccount}>Copy</button>
              </span>
            </div>
          </div>
        </div>

        <div style={styles.sectionTitle}>Your plans</div>

        {data.plans.length === 0 && <div style={styles.empty}>No plans yet.</div>}

        {data.plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            defaultOpen={onlyOnePlan}
            onPay={() => setPayingPlan(plan)}
          />
        ))}
      </div>

      {payingPlan && (
        <PayModal
          plan={payingPlan}
          plans={data.plans}
          token={token}
          bankInfo={data}
          onClose={() => setPayingPlan(null)}
          onPaid={handlePaid}
        />
      )}
    </div>
  );
}

function Chevron({ open }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function PlanCard({ plan, defaultOpen, onPay }) {
  // Single-plan customers see it expanded; with several plans they start collapsed.
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen((o) => !o);

  return (
    <div style={styles.planCard}>
      <div style={styles.planHeader}>
        <button type="button" style={styles.planToggle} onClick={toggle} aria-expanded={open}>
          <div style={styles.planIcon}>{plan.equipment_name.slice(0, 1)}</div>
          <div style={styles.planHeadText}>
            <div style={styles.planName}>{plan.equipment_name}</div>
            <div style={styles.planProgressText}>
              {plan.is_completed ? "Fully paid" : `${plan.percent_done}% paid · ${naira(plan.balance)} left`}
            </div>
          </div>
        </button>

        {plan.is_completed ? (
          <span style={styles.paidBadge}>Paid ✓</span>
        ) : (
          <button type="button" style={styles.headPayBtn} onClick={onPay}>
            Pay
          </button>
        )}

        <button type="button" style={styles.chevronBtn} onClick={toggle} aria-label={open ? "Collapse plan" : "Expand plan"}>
          <Chevron open={open} />
        </button>
      </div>

      {open && (
        <div style={styles.planBody}>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${plan.percent_done}%` }} />
          </div>

          <div style={styles.planFigures}>
            <div>
              <div style={styles.figureLabel}>Total</div>
              <div style={styles.figureValue}>{naira(plan.total_price)}</div>
            </div>
            <div>
              <div style={styles.figureLabel}>Paid</div>
              <div style={styles.figureValue}>{naira(plan.total_paid)}</div>
            </div>
            <div>
              <div style={styles.figureLabel}>Balance</div>
              <div style={styles.figureValueRed}>{naira(plan.balance)}</div>
            </div>
          </div>

          {plan.payments.length > 0 ? (
            <div style={styles.paymentHistory}>
              <div style={styles.phLabel}>Payment history</div>
              {plan.payments.map((p, i) => (
                <div key={p.id} style={{ ...styles.phRow, ...(i === 0 ? styles.phRowFirst : {}) }}>
                  <div>
                    <span style={styles.phAmount}>{naira(p.amount)}</span>
                    <span style={styles.phDate}>
                      {p.submitted_at ? new Date(p.submitted_at).toLocaleDateString("en-NG", { month: "short", day: "numeric", year: "numeric" }) : ""}
                    </span>
                  </div>
                  <span style={{ ...styles.phStatus, ...(statusColors[p.status] || {}) }}>
                    {p.status === "confirmed" ? "Confirmed" : p.status === "rejected" ? "Rejected" : "Pending"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.noHistory}>No payments yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

function PayModal({ plan, plans, token, bankInfo, onClose, onPaid }) {
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState("");
  const [amountLocked, setAmountLocked] = useState(true);
  const [receiptStatus, setReceiptStatus] = useState("");
  const [receiptStatusColor, setReceiptStatusColor] = useState(colors.ink400);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [choices, setChoices] = useState([]); // plan ids picked for the overflow, in order

  const primaryBalance = toCents(plan.balance);
  const others = plans.filter((p) => p.id !== plan.id && !p.is_completed && toCents(p.balance) > 0);
  const maxCents = primaryBalance + others.reduce((sum, p) => sum + toCents(p.balance), 0);

  const enteredCents = toCents(amount);
  const amountError =
    enteredCents > maxCents
      ? others.length > 0
        ? `That's more than your total outstanding balance (${naira(maxCents / 100)}).`
        : `That's more than your remaining balance (${naira(maxCents / 100)}).`
      : "";

  const split = buildSplit(enteredCents, plan, others, choices);
  const spills = !amountError && enteredCents > primaryBalance;

  function formatAmountInput(raw) {
    const digits = raw.replace(/[^\d.]/g, "");
    const parts = digits.split(".");
    const intPart = parts[0].replace(/^0+(?=\d)/, "");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? withCommas + "." + parts[1].slice(0, 2) : withCommas;
  }

  function handleAmountChange(e) {
    setAmount(formatAmountInput(e.target.value));
  }

  async function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setReceiptStatus("Reading receipt...");
    setReceiptStatusColor(colors.ink400);

    const fd = new FormData();
    fd.append("receipt", f);
    try {
      const result = await publicApi.analyzeReceipt(token, fd);
      if (result.amount) {
        setAmount(formatAmountInput(String(result.amount)));
        setReceiptStatus("Filled in from your receipt — please double check it before submitting.");
        setReceiptStatusColor(colors.blue700);
      } else {
        setReceiptStatus("Couldn't read an amount from that file — please enter it manually.");
        setReceiptStatusColor(colors.red700);
      }
    } catch {
      setReceiptStatus("Something went wrong — please enter the amount manually.");
      setReceiptStatusColor(colors.red700);
    } finally {
      setAmountLocked(false);
    }
  }

  const canSubmit =
    !!file && !amountLocked && !amountError && enteredCents > 0 && !split.needsChoice && split.remaining === 0;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("plan_id", plan.id);
      fd.append("amount", amount);
      fd.append(
        "allocations",
        JSON.stringify(split.allocs.map((a) => ({ plan_id: a.plan.id, amount: fromCents(a.cents) })))
      );
      if (file) fd.append("receipt", file);
      const res = await publicApi.submitPayment(token, fd);
      onPaid(res?.count || split.allocs.length || 1);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalInner}>
          <div style={styles.modalHead}>
            <div style={styles.modalTitle}>Pay towards {plan.equipment_name}</div>
            <button type="button" style={styles.modalClose} onClick={onClose}>✕</button>
          </div>

          <div style={{ ...styles.bankBox, marginTop: 0 }}>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Bank</span>
              <span>{bankInfo.bank_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account name</span>
              <span>{bankInfo.account_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account number</span>
              <span>{bankInfo.account_number}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={styles.receiptBox}>
              <label style={styles.receiptLabel}>Upload receipt (required)</label>
              <FileDropzone
                accept="image/*,application/pdf"
                required
                onChange={handleFileChange}
                title="Tap to upload your receipt"
                hint="A photo, screenshot or PDF of your transfer"
              />
              {receiptStatus && (
                <div style={{ ...styles.receiptStatus, color: receiptStatusColor }}>{receiptStatus}</div>
              )}
            </div>

            <label style={styles.formLabel}>Amount sent (₦)</label>
            <input
              type="text"
              inputMode="decimal"
              required
              readOnly={amountLocked}
              placeholder={amountLocked ? "Upload a receipt first" : "e.g. 400,000"}
              value={amount}
              onChange={handleAmountChange}
              style={styles.formInput}
            />
            {amountError && <div style={styles.amountError}>{amountError}</div>}
            {!amountError && !spills && others.length > 0 && !amountLocked && (
              <div style={styles.amountHint}>
                {plan.equipment_name} balance: {naira(plan.balance)}. Anything above that can go to your other plans.
              </div>
            )}
            {submitError && <div style={styles.amountError}>{submitError}</div>}

            {spills && (
              <div style={styles.splitBox}>
                <div style={styles.splitTitle}>
                  This is more than your {plan.equipment_name} balance ({naira(plan.balance)})
                </div>

                {split.steps.map((step, i) => (
                  <div key={i} style={{ marginTop: 10 }}>
                    <div style={styles.splitHint}>
                      Where should the remaining {naira(step.remainingBefore / 100)} go?
                    </div>
                    {step.candidates.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        style={{ ...styles.choiceCard, ...(step.selectedId === c.id ? styles.choiceCardActive : {}) }}
                        onClick={() => setChoices((prev) => [...prev.slice(0, i), c.id])}
                      >
                        <span style={{ fontWeight: 700 }}>{c.equipment_name}</span>
                        <span style={{ color: colors.ink400 }}>{naira(c.balance)} left</span>
                      </button>
                    ))}
                  </div>
                ))}

                {split.allocs.length > 0 && (
                  <div style={styles.splitSummary}>
                    <div style={{ ...styles.splitHint, marginBottom: 4 }}>Your payment will be split:</div>
                    {split.allocs.map((a) => (
                      <div key={a.plan.id} style={styles.splitRow}>
                        <span>{a.plan.equipment_name}</span>
                        <strong>{naira(a.cents / 100)}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              style={{ ...styles.submitBtn, ...((!canSubmit || submitting) ? styles.submitBtnDisabled : {}) }}
              disabled={!canSubmit || submitting}
            >
              {submitting
                ? "Sending..."
                : amountLocked
                ? "Upload your receipt first"
                : amountError
                ? "Amount exceeds balance"
                : split.needsChoice
                ? "Choose where the rest goes"
                : "I've sent this"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}